"use client"

import { observer, use$ } from "@legendapp/state/react"
import { Avatar, Group, Stack, Text } from "@mantine/core"
import { Spotlight } from "@mantine/spotlight"
import { IconFlask2, IconFlaskOff, IconRefresh } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"

import classes from "./HeaderSearchSpotlightActions.module.css"
import { showNotification } from "@/lib/notifications"
import { headerSearchSpotlight } from "./HeaderSearchSpotlight"
import { statsSampleDataDevOverride$, type TStatsSampleDataOverrideMode } from "@/features/Stats/statsSampleDataDevOverride.store"
import { pagedUserStatsQueryKeyPrefix, statsSampleGateQueryKeyPrefix, userStatsQueryKeyPrefix } from "@/hooks/queries/stats/statsQueryKeys"

export function shouldShowStatsSampleDataDevActions(query: string) {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return false

  if (normalized === "/" || normalized.startsWith("/")) return true

  if (normalized.startsWith("dev sample") || normalized.startsWith("dev stats")) return true
  if (normalized.includes("sample data")) return true
  if (normalized.includes("stats sample")) return true
  if (normalized.includes("demo stats")) return true
  if (normalized.includes("sample") && normalized.includes("stats")) return true

  return false
}

export const HeaderSearchSpotlightDevActions = observer(function HeaderSearchSpotlightDevActions() {
  const queryClient = useQueryClient()
  const currentMode = use$(() => statsSampleDataDevOverride$.sampleDataMode.get())

  const applyOverride = (mode: TStatsSampleDataOverrideMode, title: string, message: string) => {
    statsSampleDataDevOverride$.sampleDataMode.set(mode)

    queryClient.invalidateQueries({ queryKey: userStatsQueryKeyPrefix })
    queryClient.invalidateQueries({ queryKey: pagedUserStatsQueryKeyPrefix })
    queryClient.invalidateQueries({ queryKey: statsSampleGateQueryKeyPrefix })

    showNotification({
      type: "success",
      title,
      message,
    })

    headerSearchSpotlight.close()
  }

  const modeLabel = currentMode === "force_on" ? "forced on" : currentMode === "force_off" ? "forced off" : "automatic"

  return (
    <>
      <Spotlight.Action
        classNames={{
          action: classes.container,
        }}
        onClick={() => applyOverride("force_on", "Stats sample data enabled", "Sample data is now forced on for Stats (dev override).")}
      >
        <Group wrap="nowrap" w="100%">
          <Avatar size="md" color="grape" classNames={{ placeholder: classes.icon }}>
            <IconFlask2 size={16} />
          </Avatar>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={550} className={classes.text}>
              / Enable Stats Sample Data (Dev)
            </Text>
            <Text size="xs" c="dimmed" className={classes.subtext}>
              Force sample data on. Current mode: {modeLabel}
            </Text>
          </Stack>
        </Group>
      </Spotlight.Action>

      <Spotlight.Action
        classNames={{
          action: classes.container,
        }}
        onClick={() => applyOverride("force_off", "Stats sample data disabled", "Sample data is now forced off for Stats (dev override).")}
      >
        <Group wrap="nowrap" w="100%">
          <Avatar size="md" color="red" classNames={{ placeholder: classes.icon }}>
            <IconFlaskOff size={16} />
          </Avatar>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={550} className={classes.text}>
              / Disable Stats Sample Data (Dev)
            </Text>
            <Text size="xs" c="dimmed" className={classes.subtext}>
              Force sample data off. Current mode: {modeLabel}
            </Text>
          </Stack>
        </Group>
      </Spotlight.Action>

      <Spotlight.Action
        classNames={{
          action: classes.container,
        }}
        onClick={() => applyOverride("auto", "Stats sample data reset", "Stats sample data is back to automatic mode.")}
      >
        <Group wrap="nowrap" w="100%">
          <Avatar size="md" color="blue" classNames={{ placeholder: classes.icon }}>
            <IconRefresh size={16} />
          </Avatar>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={550} className={classes.text}>
              / Reset Stats Sample Data to Automatic (Dev)
            </Text>
            <Text size="xs" c="dimmed" className={classes.subtext}>
              Use normal behavior (sample data disappears after outbound calls).
            </Text>
          </Stack>
        </Group>
      </Spotlight.Action>
    </>
  )
})
